import { useState, useEffect } from 'react';
import { CreatePartnerPayload, Partner, PartnerCategory, UpdatePartnerPayload } from 'services/partner/partner.types';
import { extractErrorMessage } from 'utils/errors';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useCreatePartnerMutation,
    useGetAllPartnersMutation,
    useGetPartnerMutation,
    useUpdatePartnerMutation
} from 'services/partner/partner.endpoint';
import { useApiToast } from 'components/Toaster';

export const usePartnerFormVM = () => {
    const { partnerId } = useParams();
    const navigate = useNavigate();
    const [partnerCategories, setPartnerCategories] = useState<PartnerCategory[]>([]);
    const [partner, setPartner] = useState<Partner | null>(null);
    const [lastUpdatedPartnerName, setLastUpdatedPartnerName] = useState<string>('');
    const [lastCreatedPartnerName, setLastCreatedPartnerName] = useState<string>('');
    const { showApiToast, showErrorToast } = useApiToast();

    const handlePartnerSubmit = async (data: typeof partner extends null ? CreatePartnerPayload : UpdatePartnerPayload) => {
        let result;
        if (partner) {
            setLastUpdatedPartnerName(partner.name);
            result = await updatePartner({ ...data, id: partner.id });
        } else {
            setLastCreatedPartnerName(data.name);
            result = await createPartner(data);
        }
    };

    const [
        createPartner,
        {
            isLoading: isCreatePartnerLoading,
            isError: isCreatePartnerError,
            isSuccess: isCreatePartnerSuccess,
            error: createPartnerError,
            data: createPartnerData
        }
    ] = useCreatePartnerMutation();

    const [
        getAllPartners,
        {
            isLoading: isPartnersLoading,
            isError: isPartnersError,
            isSuccess: isPartnersSuccess,
            error: partnersError,
            data: partnersResponse
        }
    ] = useGetAllPartnersMutation();

    const [
        getPartner,
        {
            isLoading: isGetPartnerLoading,
            isError: isGetPartnerError,
            isSuccess: isGetPartnerSuccess,
            error: getPartnerError,
            data: getPartnerData
        }
    ] = useGetPartnerMutation();

    const [
        updatePartner,
        {
            isLoading: isUpdatePartnerLoading,
            isError: isUpdatePartnerError,
            isSuccess: isUpdatePartnerSuccess,
            error: updatePartnerError,
            data: updatePartnerData
        }
    ] = useUpdatePartnerMutation();

    useEffect(() => {
        if (partnerId) {
            getPartner({ id: parseInt(partnerId) });
        }
    }, [partnerId]);

    useEffect(() => {
        getAllPartners(undefined);
    }, []);

    useEffect(() => {
        if (isCreatePartnerLoading) return;
        if (isCreatePartnerError && createPartnerError) {
            showErrorToast(createPartnerError, {
                message: `Erreur lors de la création du partenaire "${lastCreatedPartnerName}"`
            });
        }
        if (isCreatePartnerSuccess && createPartnerData) {
            showApiToast(createPartnerData, {
                successMessage: `Partenaire "${lastCreatedPartnerName}" créé avec succès`,
                errorMessage: `Erreur lors de la création du partenaire "${lastCreatedPartnerName}"`
            });
            navigate(`/app/admin/partners`);
            return;
        }
    }, [isCreatePartnerLoading, isCreatePartnerError, isCreatePartnerSuccess, createPartnerError, createPartnerData]);

    useEffect(() => {
        if (isPartnersLoading) return;
        if (isPartnersError && partnersError) {
            showErrorToast(partnersError, {
                message: 'Erreur de chargement des partenaires'
            });
            return;
        }
        if (isPartnersSuccess && partnersResponse) {
            setPartnerCategories(
                partnersResponse.data.map((category) => ({
                    id: category.id,
                    label: category.label
                }))
            );
            return;
        }
    }, [isPartnersLoading, isPartnersError, isPartnersSuccess, partnersError, partnersResponse]);

    useEffect(() => {
        if (isGetPartnerLoading) return;
        if (isGetPartnerError && getPartnerError) {
            showErrorToast(getPartnerError, {
                message: 'Erreur de chargement du partenaire'
            });
        }
        if (isGetPartnerSuccess && getPartnerData) {
            setPartner(getPartnerData.data);
            return;
        }
    }, [isGetPartnerLoading, isGetPartnerError, getPartnerError, getPartnerData]);

    useEffect(() => {
        if (isUpdatePartnerLoading) return;
        if (isUpdatePartnerError && updatePartnerError) {
            showErrorToast(updatePartnerError, {
                message: `Erreur lors de la mise à jour du partenaire "${lastUpdatedPartnerName}"`
            });
        }
        if (isUpdatePartnerSuccess && updatePartnerData) {
            showApiToast(updatePartnerData, {
                successMessage: `Partenaire "${lastUpdatedPartnerName}" mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour du partenaire "${lastUpdatedPartnerName}"`
            });
            navigate(`/app/admin/partners`);

            return;
        }
    }, [isUpdatePartnerLoading, isUpdatePartnerError, updatePartnerError, updatePartnerData]);

    return {
        handlePartnerSubmit,
        partnerCategories,
        partner,
        isLoading: isCreatePartnerLoading || isUpdatePartnerLoading || isGetPartnerLoading,
    };};
